import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { LOADING_STATUSES as STATUS } from "constants/common";
import { CentralEmailThreadsService } from "services/central-email-threads-service";

export const loadEmailThreads = createAsyncThunk(
  "GET_CENTRAL_EMAIL_THREADS",
  async ({
    page,
    fromEmail,
    supportAgentId,
    startDate,
    endDate,
    sortBy,
    sortOrder,
  }) => {
    const dataConfig = {
      params: {
        page,
        per_page: 25,
        from_email: fromEmail || "",
        // 0 is the API's word for unassigned, so it has to survive the
        // fallback that blanks every other missing filter.
        support_agent_id: supportAgentId ?? "",
        start_date: startDate || "",
        end_date: endDate || "",
        sort_by: sortBy || "created_at",
        order: sortOrder || "desc",
      },
    };
    const response = await CentralEmailThreadsService.getEmailThreads(
      dataConfig
    );

    return response.data;
  }
);

export const loadEmailThread = createAsyncThunk(
  "GET_CENTRAL_EMAIL_THREAD",
  async (id) => {
    const response = await CentralEmailThreadsService.getEmailThread(id);

    return response.data;
  }
);

export const assignEmailThreadAgent = createAsyncThunk(
  "PATCH_CENTRAL_EMAIL_THREAD_AGENT",
  async ({ id, supportAgentId }) => {
    // The API needs the key present even to unassign, so null is sent
    // explicitly rather than left out.
    const response = await CentralEmailThreadsService.patchEmailThread(id, {
      data: {
        attributes: {
          support_agent_id:
            supportAgentId === null ? null : Number(supportAgentId),
        },
      },
    });

    // The service has already told the agent what went wrong; failing the
    // thunk leaves the stored assignee as it was.
    if (!response) throw new Error("The assignee could not be changed");

    return response.data;
  }
);

const initialState = {
  error: "",
  status: null,
  emailThreads: {},
  selectedThread: null,
  selectedThreadId: null,
  selectedThreadStatus: null,
};

const centralEmailThreadsSlice = createSlice({
  name: "centralEmailThreads",
  initialState,
  reducers: {
    clearSelectedThread: (state) => {
      state.selectedThread = null;
      state.selectedThreadId = null;
      state.selectedThreadStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadEmailThreads.pending, (state) => {
        state.status = STATUS.loading;
      })
      .addCase(loadEmailThreads.fulfilled, (state, action) => {
        state.status = STATUS.success;
        state.emailThreads = action.payload;
      })
      .addCase(loadEmailThreads.rejected, (state, action) => {
        state.status = STATUS.failed;
        state.error = action.payload;
      })
      .addCase(loadEmailThread.pending, (state, action) => {
        state.selectedThreadId = String(action.meta.arg);
        state.selectedThreadStatus = STATUS.loading;
      })
      // Threads are read down a queue, so a slow request is often overtaken by
      // the next one. Only the thread currently asked for may write here —
      // otherwise a late response shows one thread under another's subject.
      .addCase(loadEmailThread.fulfilled, (state, action) => {
        if (String(action.meta.arg) !== state.selectedThreadId) return;

        state.selectedThreadStatus = STATUS.success;
        state.selectedThread = action.payload;
      })
      .addCase(loadEmailThread.rejected, (state, action) => {
        if (String(action.meta.arg) !== state.selectedThreadId) return;

        state.selectedThreadStatus = STATUS.failed;
        state.error = action.error.message;
      })
      // The patch answers with the thread alone, so only the assignee is
      // copied across: replacing the stored thread would drop its emails.
      .addCase(assignEmailThreadAgent.fulfilled, (state, action) => {
        const updated = action.payload?.data;
        const id = String(action.meta.arg.id);
        const supportAgentId = updated?.attributes?.support_agent_id ?? null;

        if (String(state.selectedThread?.data?.id) === id) {
          state.selectedThread.data.attributes.support_agent_id =
            supportAgentId;
        }

        const listed = state.emailThreads?.data?.find(
          (thread) => String(thread.id) === id
        );
        if (listed?.attributes) {
          listed.attributes.support_agent_id = supportAgentId;
        }
      });
  },
});

export const { clearSelectedThread } = centralEmailThreadsSlice.actions;

export const getEmailThreadsData = (state) =>
  state.centralEmailThreads.emailThreads;
export const getEmailThreadsStatus = (state) =>
  state.centralEmailThreads.status;
export const getSelectedThread = (state) =>
  state.centralEmailThreads.selectedThread;
export const getSelectedThreadStatus = (state) =>
  state.centralEmailThreads.selectedThreadStatus;

export default centralEmailThreadsSlice.reducer;
